import React, { useEffect, useRef, useState } from "react";
import { AppAPIService } from "../../../../services/AppAPIService";
import styles from "./collections.module.scss";
import voice from "./voice.module.scss";

const request = (config) =>
  AppAPIService.getInstance().makeHttpRequest({ ...config, sensitive: true });
const endpoint = "/financial/voice-archive/test";
const terminal = (r) =>
  ["ended", "error", "not_connected"].includes(r?.callStatus);
const canFinish = (r) =>
  terminal(r) || ["RATE_LIMITED", "NOT_SENT"].includes(r?.state);
const uuid = () => {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const s = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${s.slice(0, 8)}-${s.slice(8, 12)}-${s.slice(12, 16)}-${s.slice(
    16,
    20
  )}-${s.slice(20)}`;
};
const validPhone = (phone) => {
  if (!/^[+\d ()-]{10,24}$/.test(phone)) return false;
  const raw = phone.replace(/\D/g, "");
  const number = /^\d{10,11}$/.test(raw) ? "55" + raw : raw;
  return (
    /^55[1-9][1-9](?:[2-5]\d{7}|9\d{8})$/.test(number) &&
    number !== "554138911910"
  );
};
const message = (r) => {
  if (r?.callStatus)
    return (
      {
        registered: "Ligação solicitada. Aguarde o telefone tocar.",
        ongoing: "Ligação em andamento.",
        ended: "Ligação encerrada.",
        error: "A ligação terminou com falha.",
        not_connected: "A ligação não foi conectada.",
      }[r.callStatus] || "Aguardando atualização da ligação."
    );
  return (
    {
      ACCEPTED: "Ligação solicitada. Aguarde o telefone tocar.",
      RATE_LIMITED:
        "Aguarde antes de outro teste: um por minuto no portal e um por número a cada intervalo de dez minutos.",
      NOT_SENT: "O teste não foi enviado. Você pode preparar outro.",
      RESERVED: "Solicitação em conferência. Não repita a ligação.",
      DISPATCHING: "Solicitação em conferência. Não repita a ligação.",
      UNCERTAIN:
        "Não foi possível confirmar o resultado. A ligação pode ter sido iniciada. Não repita; confira o histórico.",
    }[r?.state] || "Conferindo solicitação…"
  );
};

export default function VoiceAgentTest({
  userKey,
  api = request,
  onComplete = () => {},
}) {
  const storageKey = `xavier-voice-test-${userKey}`;
  const [config, setConfig] = useState(null);
  const [configError, setConfigError] = useState(false);
  const [phone, setPhone] = useState("");
  const [review, setReview] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [requestId, setRequestId] = useState(() => {
    try {
      return sessionStorage.getItem(storageKey) || "";
    } catch {
      return "";
    }
  });
  const mounted = useRef(true);
  const inFlight = useRef(false);
  const notified = useRef("");
  const complete = useRef(onComplete);
  complete.current = onComplete;
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    let active = true;
    Promise.resolve()
      .then(() => api({ method: "GET", url: `${endpoint}/configuration` }))
      .then((value) => {
        if (
          typeof value?.enabled !== "boolean" ||
          value.scenario !== "PRESENTATION_ONLY" ||
          value.fromNumber !== "+554138911910"
        )
          throw new Error();
        if (active) setConfig(value);
      })
      .catch(() => {
        if (active) setConfigError(true);
      });
    return () => {
      active = false;
    };
  }, [api]);
  useEffect(() => {
    if (
      !requestId ||
      terminal(result) ||
      ["RATE_LIMITED", "NOT_SENT"].includes(result?.state)
    )
      return undefined;
    let active = true;
    let timer;
    let attempts = 0;
    const poll = async () => {
      try {
        const value = await api({
          method: "GET",
          url: `${endpoint}/requests/${requestId}`,
        });
        if (
          value?.requestId !== requestId ||
          ![
            "RESERVED",
            "DISPATCHING",
            "UNCERTAIN",
            "ACCEPTED",
            "RATE_LIMITED",
            "NOT_SENT",
          ].includes(value.state)
        )
          throw new Error();
        if (active) {
          setResult(value);

        }
        if (terminal(value) || canFinish(value)) return;
      } catch {
        /* An unavailable status never authorizes a second POST. */
      }
      if (active && ++attempts < 60) timer = setTimeout(poll, 5000);
    };
    timer = setTimeout(poll, 1500);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [requestId, api, result?.callStatus, result?.state]);
  useEffect(() => {
    if (!requestId || !terminal(result)) return;
    sessionStorage.removeItem(storageKey);
    setRequestId("");
    setResult(null);
    setPhone("");
    setReview(false);
    setConfirmed(false);
    setError("");
    if (notified.current !== requestId) {
      notified.current = requestId;
      complete.current();
    }
  }, [requestId, result, storageKey]);
  const send = async () => {
    if (
      inFlight.current ||
      requestId ||
      !confirmed ||
      !review ||
      !validPhone(phone) ||
      !config?.enabled
    )
      return;
    inFlight.current = true;
    setBusy(true);
    setError("");
    let id;
    try {
      id = uuid();
      // Save before POST. Reloading resumes GET-only tracking, never the call.
      sessionStorage.setItem(storageKey, id);
      setRequestId(id);
      setResult({ requestId: id, state: "DISPATCHING" });
      const value = await api({
        method: "POST",
        url: `${endpoint}/calls`,
        data: {
          requestId: id,
          phone,
          scenario: "PRESENTATION_ONLY",
          confirmed: true,
        },
      });
      if (
        value?.requestId !== id ||
        ![
          "RESERVED",
          "DISPATCHING",
          "UNCERTAIN",
          "ACCEPTED",
          "RATE_LIMITED",
          "NOT_SENT",
        ].includes(value.state)
      )
        throw new Error();
      if (mounted.current) setResult(value);
    } catch (failure) {
      if (mounted.current) {
        if (id && [400, 403, 503].includes(failure?.response?.status)) {
          // These HTTP errors occur before dispatch; provider ambiguity returns a state instead.
          setResult({ requestId: id, state: "NOT_SENT" });
          setError(
            "O servidor não liberou o teste. Confira o número, seu acesso e a disponibilidade do agente."
          );
        } else if (id) {
          setResult({ requestId: id, state: "UNCERTAIN" });
          setError(
            "Não repetiremos a solicitação automaticamente. Confira o telefone e o histórico antes de qualquer nova tentativa."
          );
        } else setError("Não foi possível preparar o teste neste navegador.");
      }
    } finally {
      inFlight.current = false;
      if (mounted.current) setBusy(false);
    }
  };
  return (
    <section className={`${styles.list} ${voice.testPanel}`} aria-label="Teste do agente">
      <div className={styles.listHeader}>
        <div>
          <span>Somente administradores</span>
          <h2>Testar agente por telefone</h2>
        </div>
      </div>
      <div className={voice.intro}>
        <p>
          Receba uma ligação real do número <strong>+55 (41) 3891-1910</strong>{" "}
          para conversar com o agente. O teste tem custo na Retell e na Twilio e
          dura até três minutos.
        </p>
        <p>
          {config?.testScript === "COLLECTION_SIMULATION_V1" ? (
            <><strong>Cenário: simulação de atendimento financeiro.</strong>{" "}
              O agente passa pela triagem do iPhone, se identifica e avisa sobre a gravação e o teste.
              Você pode simular pagamento já realizado, pedido de boleto, dificuldade para pagar,
              contestação ou atendimento humano. Usamos somente uma parcela fictícia de R$ 250,00,
              vencida em 15/10/2026. Nenhuma cobrança real será consultada, negociada ou enviada.</>
          ) : (
            <><strong>Cenário: apresentação e triagem.</strong>{" "}
              O agente se identifica, avisa sobre a gravação e pode passar pela triagem do iPhone.
              Como a cobrança ainda não está integrada, ele encerra antes de informar dívidas ou negociar.
              Nenhum dado financeiro de cliente será utilizado.</>
          )}
        </p>
        <p>
          O áudio, a transcrição, a duração e os custos aparecerão neste
          relatório após o processamento. Se algum dado ainda não estiver
          disponível, ficará pendente.
        </p>
        {configError && (
          <p role="alert">
            Não foi possível conferir a disponibilidade dos testes. Atualize a
            página.
          </p>
        )}
        {!config && !configError && (
          <p role="status">Conferindo disponibilidade…</p>
        )}
        {config && !config.enabled && (
          <p role="status">Os testes por telefone ainda não estão liberados.</p>
        )}
        {error && <p role="alert">{error}</p>}
        {requestId ? (
          <div role="status" aria-live="polite">
            <strong>{message(result)}</strong>
            {result?.destinationMasked && (
              <p>Destino: {result.destinationMasked}</p>
            )}
            {result?.callId && (
              <p>
                Identificação: <code>{result.callId}</code>
              </p>
            )}
            {terminal(result) && (
              <p>
                {result.archivePending
                  ? "Arquivo em processamento. A captura automática tentará novamente; atualize o relatório em alguns minutos."
                  : "Registro arquivado. Atualize o relatório para conferir mídias e custos disponíveis."}
              </p>
            )}
            {!canFinish(result) && (
              <p>
                O acompanhamento atualiza automaticamente por até cinco minutos.
                Se não houver confirmação, confira o histórico; não haverá nova
                ligação automática.
              </p>
            )}
            {canFinish(result) && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  sessionStorage.removeItem(storageKey);
                  setRequestId("");
                  setResult(null);
                  setReview(false);
                  setConfirmed(false);
                  setError("");
                }}
              >
                Preparar outro teste
              </button>
            )}
          </div>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!review) setReview(true);
            }}
          >
            <div className={voice.testFields}>
              <label htmlFor="voice-test-phone">
                Seu telefone com DDD
                <input
                  id="voice-test-phone"
                  className="form-control"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="(41) 99999-9999"
                  value={phone}
                  disabled={review || busy}
                  onChange={(e) => setPhone(e.target.value)}
                  required
                  maxLength={24}
                />
              </label>
              {!review && (
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={!config?.enabled || !validPhone(phone)}
                >
                  Revisar teste
                </button>
              )}
            </div>
            {review && (
              <div className={voice.testConfirmation}>
                <p>
                  <strong>Confirmar uma ligação para {phone}?</strong> Ela será
                  gravada e cobrada pelos provedores.
                </p>
                <label>
                  <input
                    type="checkbox"
                    checked={confirmed}
                    onChange={(e) => setConfirmed(e.target.checked)}
                  />{" "}
                  <span>Este número é meu ou da equipe que autorizou o teste e está
                  aguardando a ligação.</span>
                </label>
                <div className={voice.testActions}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={busy}
                    onClick={() => {
                      setReview(false);
                      setConfirmed(false);
                    }}
                  >
                    Voltar
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={!confirmed || busy || !config?.enabled}
                    onClick={send}
                  >
                    {busy ? "Solicitando…" : "Confirmar e ligar agora"}
                  </button>
                </div>
              </div>
            )}
          </form>
        )}
      </div>
    </section>
  );
}
